import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { setCurrentUser } from './currentUserSlice';
import type { UndecoratedFinancialReport } from '@flemingmetrics/api-client';
import { filterByValue, mergeWithoutDuplicates } from '@/utils/arrays';

type FinancialReportsState = {
  byId: Record<string, UndecoratedFinancialReport>;
  byCompanyId: Record<string, string[]>;
};

const initialState: FinancialReportsState = {
  byId: {},
  byCompanyId: {},
};

export const financialReportsSlice = createSlice({
  name: 'financialReports',
  initialState,
  reducers: {
    addFinancialReport: (
      state,
      action: PayloadAction<UndecoratedFinancialReport>
    ) => {
      return getNextState(state, [action.payload]);
    },
    setFinancialReports: (
      state,
      action: PayloadAction<UndecoratedFinancialReport[]>
    ) => {
      return getNextState(state, action.payload);
    },
    deleteFinancialReport: (
      state,
      action: PayloadAction<{ reportId: number }>
    ) => {
      const key = `${action.payload.reportId}`;
      const report = state.byId[key];
      if (!report) return state;
      const { companyId } = report;
      const byId = { ...state.byId };
      const byCompanyId = { ...state.byCompanyId };
      byCompanyId[companyId] = filterByValue(byCompanyId[companyId], key);
      delete byId[key];
      return { byId, byCompanyId };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setCurrentUser, (state, action) => {
      return { byId: {}, byCompanyId: {} };
    });
  },
});

function getNextState(
  state: FinancialReportsState,
  reports: UndecoratedFinancialReport[]
) {
  const byId = { ...state.byId };
  const byCompanyId = { ...state.byCompanyId };
  reports.forEach((report) => {
    const key = `${report.id}`;
    byId[key] = report;
    byCompanyId[report.companyId] = mergeWithoutDuplicates(
      byCompanyId[report.companyId] || [],
      [key]
    );
  });
  return { byId, byCompanyId };
}

export const {
  addFinancialReport,
  setFinancialReports,
  deleteFinancialReport,
} = financialReportsSlice.actions;

export default financialReportsSlice.reducer;
